<template>
	<Drawer ref="drawer" initialWidth="3xl" @requestUnmount="close">
		<template #header>
			<div class="w-full flex items-center justify-between gap-3">
				<div class="flex items-center gap-3">
					<RectangleStackIcon class="w-6 h-6" />
					<div class="text-lg font-semibold">{{ $t("Lesson plan library") }}</div>
				</div>
			</div>
		</template>

		<template #default="{ hide }">
			<div class="isolate flex flex-col min-h-0 h-full">
				<!-- Tabs -->
				<LessonPlanLibraryTabs
					class="px-4"
					:activeTab="activeTab"
					@change="handleTabChange"
				/>

				<!-- Main content grid -->
				<div class="overflow-y-auto flex-1 min-h-0">
					<LessonPlanMyLibrary
						v-if="activeTab === 'my-library'"
						:readOnly="true"
						:lessonPlanViewerWidth="'2xl'"
						:lessonPlanViewerWithSelectButtons="true"
						@selectLessonPlan="handleSelectLessonPlan"
						@createVariant="handleCreateVariant"
					/>
					<!-- <FollowedAuthors
						v-else-if="activeTab === 'followed-authors'"
						class="max-w-7xl"
					/> -->
					<LessonPlanDiscover
						v-else-if="activeTab === 'discover'"
						:navigateByRoute="false"
						@selectLessonPlan="handleSelectLessonPlan"
						@createVariant="handleCreateVariant"
					/>

					<div v-else-if="activeTab === 'followed-authors'" class=""></div>
				</div>

			</div>

			<div class="drawer-action-bar justify-end">
				<button type="button" class="btn btn-ghost" @click="hide">
					{{ $t("Close") }}
				</button>
			</div>
		</template>
	</Drawer>
</template>

<script lang="ts">
import { RectangleStackIcon } from "@heroicons/vue/24/outline";
import Drawer from "@/components/ui/Drawer.v2.vue";
import { mapStores } from "pinia";
import { useLessonPlansStore } from "@/stores/LessonPlans.store";
import { useLessonPlanCollectionsStore } from "@/stores/LessonPlanCollections.store";
import { useDiscoverStateStore } from '@/stores/DiscoverCollections.store';
import LessonPlanLibraryTabs from "@/components/LessonPlanLibraryTabs.vue";
import LessonPlanMyLibrary from "@/components/LessonPlanMyLibrary.vue";
import LessonPlanDiscover from "@/components/LessonPlanDiscover.vue";

export default {
	emits: ["requestClose", "selectLessonPlan", "createVariant"],

	components: {
		Drawer,
		RectangleStackIcon,
		LessonPlanLibraryTabs,
		LessonPlanMyLibrary,
		LessonPlanDiscover,
	},

	data() {
		return {
			activeTab: "my-library",
			selectedCollection: null as any,
			loaded: false,
			pendingRequestId: 0, // Track the latest filter request
		};
	},

	computed: {
		...mapStores(useLessonPlansStore, useLessonPlanCollectionsStore, useDiscoverStateStore),
	},

	methods: {
		close() {
			this.$emit("requestClose");
		},

		handleTabChange(tabName: string) {
			this.activeTab = tabName;
		},

		// Method to trigger animated close from parent component
		animatedClose() {
			const drawer = this.$refs.drawer as any;
			if (drawer && drawer.prepareToClose) {
				drawer.prepareToClose();
			}
		},

		async handleCollectionSelection(collection: any) {
			// Increment request ID to track this specific filter change
			const requestId = ++this.pendingRequestId;

			const filter = this.lessonPlansStore.getCurrentFilter();

			// Single selection mode - replace current filter
			this.selectedCollection = collection;
			filter.collectionIds = collection?.id ? [collection.id] : [];

			// Show loading immediately
			this.loaded = false;

			try {
				await this.lessonPlansStore.applyFilter(filter);

				// Only update state if this is still the latest request
				if (requestId === this.pendingRequestId) {
					this.loaded = true;
				}
				// Otherwise, a newer request is pending, so ignore this result
			} catch (error) {
				if (requestId === this.pendingRequestId) {
					console.error('Failed to apply filter:', error);
					this.loaded = true;
				}
			}
		},

		// handleCreateIntent() {
		// 	this.$emit("newLessonPlanRequested");
		// },

		// handleViewIntent(lessonPlan: any) {
		// 	this.$emit("selectedLessonPlan", lessonPlan);
		// },

		async handleFilterChanged() {
			// Increment request ID to track this specific filter change
			const requestId = ++this.pendingRequestId;

			// Show loading immediately
			this.loaded = false;

			try {
				await this.lessonPlansStore.loadFirstPage();

				// Only update state if this is still the latest request
				if (requestId === this.pendingRequestId) {
					this.loaded = true;
				}
			} catch (error) {
				if (requestId === this.pendingRequestId) {
					console.error('Failed to reload lesson plans after filter change:', error);
					this.loaded = true;
				}
			}
		},

		handleSelectLessonPlan(lessonPlan: any) {
			this.$emit("selectLessonPlan", lessonPlan);
			this.animatedClose();
		},

		handleCreateVariant(lessonPlan: any) {
			this.$emit("createVariant", lessonPlan);
			this.animatedClose();
		},
	},
};
</script>
